import React, { useContext, useEffect, useState, useMemo } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import Loader from '../../../../../Components/Loader';
import PageHeading from '../../../../../Components/PageHeading';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import PlatformHybridConfig from './PlatformHybridConfig';
import { initialHybridAdsConfig } from '../../../../../utilities/initialConfigs';
import constants from '../../NetworkData/constants.json';

//mui imports====
import useCommonStyles from '../../../../../commonStyles';
import { Grid, Paper } from '@mui/material';
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

//===============

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: { PLATFORM_DESKTOP, PLATFORM_MOBILE, AP_BANNERSECTIONID }
} = constants;

const HybridAdsConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext); //rawConfig from ConfigContext
	const { tgmrDoc, updateTgmrDoc } = useContext(UnitsContext);
	const [selectedPlatform, setSelectedPlatform] = useState(DESKTOP);
	const [docFetchError, setDocFetchError] = useState('');
	const [isLoading, setIsLoading] = useState(true);

	let videoSectionId = rawConfig['videoSectionId'];
	const adsDoc = useMemo(() => {
		let ads = tgmrDoc?.ads?.filter(
			(ad) => ad?.framerateHybridAd?.framerateSectionId === videoSectionId
		);
		return ads || [];
	}, [tgmrDoc, videoSectionId]);

	// Fetching deskop Ap Tag Ad object from adDoc based upon the condition
	const desktopAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return adDoc?.formatData?.responsivePlatform === PLATFORM_DESKTOP;
			});

			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// Fetching mobile Ap Tag Ad object from adDoc based upon the condition
	const mobileAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return adDoc?.formatData?.responsivePlatform === PLATFORM_MOBILE;
			});
			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// The useEffect will be called when the component is rendering initially and when tgmrDoc gets updated, it fetches the tgmr doc from the api
	useEffect(() => {
		if (!tgmrDoc) {
			setIsLoading(true);
			updateTgmrDoc().finally(() => setIsLoading(false));
		} else {
			setIsLoading(false);
		}
	}, [tgmrDoc]);

	useEffect(() => {
		//Check if rawConfig have hybrid ad config
		const siteConfigHasHybridAdsConfig = Object.keys(rawConfig).some((key) =>
			key.includes(keyData)
		);

		let error = '';
		let clonedRawConfig = structuredClone(rawConfig);

		// Check if adsDoc is present and hybrid ad configuration is not present in rawConfig then set default hybrid ad configuration
		if (adsDoc && adsDoc?.length > 0 && !siteConfigHasHybridAdsConfig) {
			clonedRawConfig = { ...clonedRawConfig, ...initialHybridAdsConfig };
		}

		// Updating ApTag sectionIds for different platforms to cloned raw config
		updateApTagsIds(selectedPlatform, clonedRawConfig);

		// Check if tgmrDoc is present for the current site Id else update error
		if (!tgmrDoc || !adsDoc?.length) {
			error = 'Please create an ApTag to enable Hybrid Ads';
		}
		setDocFetchError(error);
	}, [tgmrDoc, videoSectionId]);

	/**
	 * @name onPlatformChange
	 * @description The function is used to update the platform
	 * @param {string} newValue - It is the platform selected by user
	 * @param {function} event - It is the event being triggered when we update platform
	 */
	const onPlatformChange = (event, newValue) => {
		setSelectedPlatform(newValue);
		updateApTagsIds(newValue, structuredClone(rawConfig));
	};

	/**
	 * @name updateApTagsIds
	 * @description The function is used to update the ap section Id for various platforms based upon the parameter in the object.
	 * @param {string} currentPlatform - It is the current platform for which section Id is to be updated
	 * @param {object} clonedRawConfig - It is the cloned raw config in which sectionIds will be appended.
	 */
	const updateApTagsIds = (currentPlatform, clonedRawConfig) => {
		if (currentPlatform === MOBILE || currentPlatform === TABLET) {
			// Check if mobile Ap Tag object is present in tgmr doc and id key is also present
			if (mobileAdObj && mobileAdObj.id) {
				const mobileKey = keyData + '.' + currentPlatform + '.' + AP_BANNERSECTIONID;
				clonedRawConfig[mobileKey] = mobileAdObj.id;
			}
		}
		// Check if the platform is DESKTOP, desktop Ap Tag object is present and id key inside the Ap Tag object must be present
		if (currentPlatform === DESKTOP && desktopAdObj && desktopAdObj.id) {
			const desktopKey = keyData + '.' + currentPlatform + '.' + AP_BANNERSECTIONID;
			clonedRawConfig[desktopKey] = desktopAdObj.id;
		}
		updateRawConfig(clonedRawConfig);
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			{isLoading ? (
				<Loader label="Loading Hybrid Ads Config..." />
			) : docFetchError || !adsDoc.length ? (
				<Box
					sx={{
						marginTop: '4rem',
						paddingBottom: '15rem',
						textAlign: 'center',
						color: 'red',
						fontWeight: 'bold',
						fontSize: '2rem'
					}}
				>
					{docFetchError ||
						'No Hybrid Ads Config found. Please create an ApTag to enable Hybrid Ads.'}
				</Box>
			) : (
				<>
					<PageHeading
						headingName="Hybrid Ads Config"
						headingPath={['Dashboard', 'Framerate', 'Network Data', 'Hybrid Ads Config']}
						description="Manage Platform Level Hybrid Ads here"
						showInfoButton={true}
						infoMessage={featureDescription}
					/>
					<Grid
						container
						spacing={0}
						style={{ marginLeft: '5rem', marginTop: '4rem', width: '90%' }}
					>
						<Grid item xs={6}>
							<CheckBoxInput
								keyData={keyData + '.enabled'}
								label="enabled"
								disabled={false}
								defaultChecked={false}
								object={rawConfig}
								updateObject={updateRawConfig}
							/>
						</Grid>
					</Grid>
					<TabContext value={selectedPlatform}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center'
							}}
						>
							<TabList onChange={onPlatformChange}>
								<Tab label={DESKTOP} value={DESKTOP} />
								<Tab label={MOBILE} value={MOBILE} />
								<Tab label={TABLET} value={TABLET} />
							</TabList>
						</Box>
						<TabPanel value={DESKTOP}>
							<PlatformHybridConfig
								keyData={keyData + '.' + DESKTOP}
								platform={DESKTOP}
								desktopAdObj={desktopAdObj}
								isParentEnabled={rawConfig[keyData + '.enabled']}
							/>
						</TabPanel>
						<TabPanel value={MOBILE}>
							<PlatformHybridConfig
								keyData={keyData + '.' + MOBILE}
								platform={MOBILE}
								mobileAdObj={mobileAdObj}
								isParentEnabled={rawConfig[keyData + '.enabled']}
							/>
						</TabPanel>
						<TabPanel value={TABLET}>
							<PlatformHybridConfig
								keyData={keyData + '.' + TABLET}
								platform={TABLET}
								mobileAdObj={mobileAdObj}
								isParentEnabled={rawConfig[keyData + '.enabled']}
							/>
						</TabPanel>
					</TabContext>
				</>
			)}
		</Paper>
	);
};

export default HybridAdsConfig;
